import { useCallback, useEffect, useState, type CSSProperties, type RefObject } from 'react';

import { useBackHandler } from '@/lib/backButton';
import {
  fullscreenMode,
  lockLandscape,
  rotatedLayerStyle,
  unlockOrientation,
  type ScreenLike,
} from '@/lib/videoFullscreen';

/**
 * Разворот контейнера с роликом: системный там, где он есть, наш слой — где его нет.
 *
 * Вынесено из `YouTubePlayer` 30.09.2026: тем же разворотом пользуется видео файлом
 * (`VideoBlock` урока). Два пути к полному экрану, написанные врозь, разошлись бы — так уже
 * было с касанием и перетаскиванием (lib/tapPlace.ts).
 *
 * ⚠️ РАЗВОРАЧИВАЕТСЯ КОНТЕЙНЕР, А НЕ РОЛИК. Внутри контейнера и кадр, и водяной знак. Уйди в
 * полный экран сам ролик (кнопка YouTube, родные кнопки <video>) — знак пропадёт ровно тогда,
 * когда он нужнее всего (заказчик 07.09.2026: «в некоторых уходит, в некоторых есть»).
 *
 * ⚠️ ЗАПАСНОЙ ПУТЬ ОБЯЗАТЕЛЕН. На айфоне Safari разворачивает только сам элемент video — у
 * контейнера системного разворота нет вовсе. Раньше из-за этого кнопку просто прятали, и восемь
 * учеников из десяти (телефон) не могли увеличить видео. Теперь там работает наш слой. В Android
 * WebView (приложение, окно ролика) — сразу слой: системный там закрывает Capacitor
 * (`fullscreenMode`, lib/videoFullscreen.ts).
 *
 * Отличить одно от другого можно: у системного пропадает строка браузера, у нашего остаётся.
 */
export function useContainerFullscreen(containerRef: RefObject<HTMLElement | null>): {
  /** Наш слой на весь экран. */
  expanded: boolean;
  /** Системный полноэкранный режим — отдельно от нашего слоя. */
  isFullscreen: boolean;
  /** Стиль слоя в вертикали — повёрнут на 90°; иначе `undefined`. */
  rotated: CSSProperties | undefined;
  toggle: () => void;
} {
  /**
   * ⚠️ ПОБОЧНАЯ ВЫГОДА, КОТОРУЮ НЕЛЬЗЯ ПОТЕРЯТЬ. В системном полноэкранном режиме браузер
   * показывает видео сам, и наложенный поверх знак пропадает. Наш разворот — обычный слой
   * страницы: знак остаётся на месте.
   */
  const [expanded, setExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /**
   * Размер экрана — для поворота нашего слоя в вертикали (задача 6, 11.09.2026).
   * На iPhone системного разворота нет, и слой в вертикали давал полоску 16:9:
   * при заблокированном повороте телефона увеличить ролик было нельзя.
   */
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  // Следим за системным режимом: по нему рисуется значок кнопки, и выйти из
  // него можно не только кнопкой (Esc, жест, кнопка браузера).
  useEffect(() => {
    const onChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      const el = containerRef.current;
      const now = document.fullscreenElement === el || doc.webkitFullscreenElement === el;
      setIsFullscreen(now);
      // Вышли любым способом (кнопка, жест, «Назад») — отпускаем поворот экрана.
      if (!now) unlockOrientation(window.screen as unknown as ScreenLike);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [containerRef]);

  // ---- Наш разворот: выход по Esc и запрет прокрутки под ним --------------
  //
  // ⚠️ БЕЗ ЗАПРЕТА ПРОКРУТКИ РАЗВЁРНУТОЕ ВИДЕО «ЕЗДИТ». Слой закреплён на
  // экране, а страница под ним продолжает прокручиваться от касания — на
  // телефоне это выглядит как подтормаживающее видео, которое дёргается вместе
  // со страницей. Возвращаем прежнее значение, а не пустую строку: иначе
  // затираем чужое правило, если страница уже запрещала прокрутку сама.
  useEffect(() => {
    if (!expanded) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    // Повернул телефон или сменился размер окна — слой пересчитывает поворот.
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    document.addEventListener('keydown', onEsc);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [expanded]);

  // ⚠️ РАЗВЁРНУТЫЙ РОЛИК ЗАНИМАЕТ «НАЗАД» (02.10.2026). Слой живёт на состоянии, не в истории: свайп от края на
  // iPhone уводил из урока прямо из развёрнутого ролика, а «Назад» на Android — на предыдущий экран, не свернув
  // его. Пока слой открыт, обработчик стоит в стеке `lib/backButton.ts`: на iOS жест выключен (системный жест
  // обработчиков не спрашивает), на Android кнопка сворачивает ролик. Сам ролик от жеста не закрывается.
  const collapse = useCallback(() => {
    setExpanded(false);
    return true;
  }, []);
  useBackHandler(collapse, expanded);

  const toggle = () => {
    const el = containerRef.current as
      | (HTMLElement & { webkitRequestFullscreen?: () => void })
      | null;
    if (!el) return;

    const doc = document as Document & {
      webkitExitFullscreen?: () => void;
      webkitFullscreenElement?: Element | null;
    };
    const inNativeFullscreen =
      document.fullscreenElement === el || doc.webkitFullscreenElement === el;

    if (inNativeFullscreen) {
      if (typeof document.exitFullscreen === 'function') void document.exitFullscreen();
      else doc.webkitExitFullscreen?.();
      return;
    }
    if (expanded) {
      setExpanded(false);
      return;
    }
    // Android WebView (приложение и окно ролика) — сразу наш слой: системный там закрывает Capacitor.
    if (fullscreenMode(navigator.userAgent) === 'layer') {
      setExpanded(true);
      return;
    }

    // ⚠️ ЛОВИМ ОТКАЗ, А НЕ ПРОВЕРЯЕМ ПОДДЕРЖКУ ЗАРАНЕЕ. Браузер может объявить
    // метод и всё равно отказать — например, когда страница не в фокусе. Тогда
    // человек нажал бы кнопку и не получил ничего; вместо этого разворачиваем
    // своим слоем.
    // ⚠️ ПОВОРОТ — ТОЛЬКО ПОСЛЕ ВХОДА В ПОЛНЫЙ ЭКРАН: вне его браузер откажет.
    // На Android ролик ложится горизонтально и при заблокированном повороте
    // телефона; на iPhone `lock` нет — там поворачивается наш слой (ниже).
    const screenApi = window.screen as unknown as ScreenLike;
    if (typeof el.requestFullscreen === 'function') {
      el.requestFullscreen()
        .then(() => void lockLandscape({ ua: navigator.userAgent, screen: screenApi }))
        .catch(() => setExpanded(true));
      return;
    }
    if (typeof el.webkitRequestFullscreen === 'function') {
      el.webkitRequestFullscreen();
      void lockLandscape({ ua: navigator.userAgent, screen: screenApi });
      return;
    }
    setExpanded(true);
  };

  /** Наш слой в вертикали поворачивается на 90° — ролик во весь экран без поворота телефона. */
  const rotated = expanded ? rotatedLayerStyle(viewport.w, viewport.h) : undefined;

  return { expanded, isFullscreen, rotated, toggle };
}
